import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { compressImageDataUrl } from '../lib/imageUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Users, Building2, CalendarCheck, Trophy, ListTodo, CalendarOff, AlertTriangle,
  Lock, ShieldCheck, ShieldAlert, ArrowRight, Check, Send, Plus, Camera, Shirt, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, color = 'text-neutral-950', iconBg = 'bg-neutral-100', testId, onClick }) {
  return (
    <Card className={`border border-neutral-200 shadow-sm rounded-md hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer hover:-translate-y-[1px]' : ''}`}
      data-testid={testId} onClick={onClick}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 ${iconBg} rounded-sm flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-500">{label}</p>
          <p className={`text-xl font-heading font-black ${color} tracking-tight`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── EMPLOYEE DASHBOARD ─────────────────────────────────────────────
function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const [achForm, setAchForm] = useState({ brand_name: '', target: '', achieved: '', date: new Date().toISOString().slice(0, 10) });
  const [submitting, setSubmitting] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const [branchSettings, setBranchSettings] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const res = await api.get('/dashboard/employee');
      setData(res.data);
      if (user?.branch_id) {
        const sRes = await api.get(`/branch-settings/${user.branch_id}`);
        setBranchSettings(sRes.data);
      }
    } catch (err) { console.error(err); }
  };
  useEffect(() => {
    fetchData();
    // Real-time sync: refresh every 30s + on tab/window focus
    const interval = setInterval(fetchData, 30000);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isSales = ['Sales Girl', 'Sales Man'].includes(user?.job_role);
  // Photo required unless the user is Super Admin. Branch's `require_photo`
  // toggle used to gate this, but per the "Management must also selfie" policy
  // the only permanent bypass is the Super Admin role.
  const needsPhoto = user?.system_role !== 'super_admin';

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      // Auto-compress on the device so the user never has to worry about size.
      const compressed = await compressImageDataUrl(file);
      setPhotoData(compressed);
    } catch (err) {
      toast.error('Could not read photo. Please try again.');
    }
  };

  const markAttendance = async () => {
    if (needsPhoto && !photoData) { toast.error('Take your in-store photo first'); return; }
    try {
      const payload = { date: new Date().toISOString().slice(0, 10), status: 'present' };
      if (photoData) payload.photo_base64 = photoData;
      await api.post('/attendance/mark', payload);
      toast.success('Attendance marked!');
      setPhotoData(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitAchievement = async () => {
    if (!achForm.brand_name || !achForm.target || !achForm.achieved) { toast.error('Fill all fields'); return; }
    setSubmitting(true);
    try {
      await api.post('/achievements', { ...achForm, target: parseFloat(achForm.target), achieved: parseFloat(achForm.achieved) });
      toast.success('Achievement submitted!');
      setAchForm({ brand_name: '', target: '', achieved: '', date: new Date().toISOString().slice(0, 10) });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!data) return <DashboardSkeleton />;

  const statusColor = data.eligibility?.status === 'red' ? 'border-red-300 bg-red-50' : data.eligibility?.status === 'yellow' ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50';
  const statusIcon = data.eligibility?.status === 'red' ? ShieldAlert : data.eligibility?.status === 'yellow' ? AlertTriangle : ShieldCheck;
  const StatusIcon = statusIcon;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="employee-dashboard">
      <div>
        <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">
          {isSales ? 'My Day' : 'Dashboard'}
        </h1>
        <p className="text-neutral-500 text-sm mt-0.5">Welcome, {user?.name} &middot; {user?.job_role}</p>
      </div>

      {/* Eligibility Status Banner (Sales only) */}
      {isSales && data.eligibility && (
        <Card className={`border ${statusColor} shadow-sm rounded-md`} data-testid="eligibility-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <StatusIcon className={`h-5 w-5 flex-shrink-0 ${data.eligibility.status === 'red' ? 'text-red-600' : data.eligibility.status === 'yellow' ? 'text-amber-600' : 'text-emerald-600'}`} />
            <div className="flex-1">
              <p className="text-sm font-semibold">
                {data.eligibility.status === 'red' ? 'Attendance Locked' : data.eligibility.status === 'yellow' ? 'At Risk — Update Soon' : 'You\'re Good'}
              </p>
              {data.eligibility.message && <p className="text-xs mt-0.5 text-neutral-600">{data.eligibility.message}</p>}
            </div>
            <Badge className={`text-xs ${data.eligibility.status === 'red' ? 'bg-red-200 text-red-800' : data.eligibility.status === 'yellow' ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
              {data.eligibility.status === 'red' ? 'LOCKED' : data.eligibility.status === 'yellow' ? 'WARNING' : 'ELIGIBLE'}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Mark Attendance - 1 tap */}
        <Card className={`border shadow-sm rounded-md ${data.attendance_today ? 'border-emerald-200 bg-emerald-50/50' : 'border-neutral-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Attendance</p>
              {data.attendance_today && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Done</Badge>}
            </div>
            {data.attendance_today ? (
              <p className="text-sm text-emerald-700 font-medium flex items-center gap-1.5"><Check className="h-4 w-4" /> Marked Present</p>
            ) : (
              <div className="space-y-2">
                {/* Camera capture for in-store photo */}
                {needsPhoto && !photoData && (
                  <>
                    <label
                      htmlFor="quick-attendance-photo-input"
                      className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-red-300 rounded-sm cursor-pointer hover:border-red-500 hover:bg-red-50/50 transition-colors select-none"
                      data-testid="quick-attendance-photo-label">
                      <Camera className="h-5 w-5 text-red-500" />
                      <span className="text-sm font-medium text-red-600">Take In-Store Photo</span>
                    </label>
                    <input
                      id="quick-attendance-photo-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoCapture}
                      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                      data-testid="quick-attendance-photo-input"
                    />
                  </>
                )}
                {needsPhoto && photoData && (
                  <div className="flex items-center gap-2 py-1.5">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-xs text-emerald-600 font-medium">Photo captured</span>
                    <button onClick={() => setPhotoData(null)} className="text-xs text-neutral-400 hover:text-red-500 ml-auto">Remove</button>
                  </div>
                )}
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 text-sm"
                  onClick={markAttendance} disabled={data.eligibility?.eligible === false || (needsPhoto && !photoData)}
                  data-testid="quick-mark-attendance">
                  <CalendarCheck className="h-4 w-4 mr-1.5" /> Mark Present
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Tasks */}
        <Card className="border border-neutral-200 shadow-sm rounded-md cursor-pointer hover:shadow-md transition-all"
          onClick={() => navigate('/tasks')}>
          <CardContent className="p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Tasks</p>
            <p className="text-xl font-heading font-black text-blue-600">{data.pending_tasks}</p>
            <p className="text-xs text-neutral-400 flex items-center gap-1 mt-1">pending <ArrowRight className="h-3 w-3" /></p>
          </CardContent>
        </Card>

        {/* Apply Leave */}
        <Card className="border border-neutral-200 shadow-sm rounded-md cursor-pointer hover:shadow-md transition-all"
          onClick={() => navigate('/leave?view=all&status=pending,manager_approved')}>
          <CardContent className="p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Leave</p>
            <p className="text-xl font-heading font-black text-purple-600">{data.pending_leaves}</p>
            <p className="text-xs text-neutral-400 flex items-center gap-1 mt-1">pending <ArrowRight className="h-3 w-3" /></p>
          </CardContent>
        </Card>
      </div>

      {/* Inline Achievement Submission (Sales Staff - submit in <10 seconds) */}
      {isSales && (
        <Card className="border border-red-200 shadow-sm rounded-md bg-white" data-testid="quick-achievement-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-red-600" /> Quick Submit Achievement
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-col sm:flex-row gap-2.5">
              <Input placeholder="Brand" value={achForm.brand_name}
                onChange={e => setAchForm(p => ({ ...p, brand_name: e.target.value }))}
                className="rounded-sm h-9 text-sm flex-1" data-testid="quick-ach-brand" />
              <Input placeholder="Target" type="number" value={achForm.target}
                onChange={e => setAchForm(p => ({ ...p, target: e.target.value }))}
                className="rounded-sm h-9 text-sm w-full sm:w-24" data-testid="quick-ach-target" />
              <Input placeholder="Achieved" type="number" value={achForm.achieved}
                onChange={e => setAchForm(p => ({ ...p, achieved: e.target.value }))}
                className="rounded-sm h-9 text-sm w-full sm:w-24" data-testid="quick-ach-achieved" />
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-5 text-sm"
                onClick={submitAchievement} disabled={submitting} data-testid="quick-ach-submit">
                <Send className="h-3.5 w-3.5 mr-1.5" /> {submitting ? 'Sending...' : 'Submit'}
              </Button>
            </div>
            <p className="text-[10px] text-neutral-400 mt-2">Date: {achForm.date} &middot; Submitted for manager approval</p>
          </CardContent>
        </Card>
      )}

      {/* Recent Submissions */}
      {isSales && data.recent_achievements?.length > 0 && (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-neutral-700">Recent Submissions</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-red-600" onClick={() => navigate('/achievements')}>
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {data.recent_achievements.map(a => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="text-right min-w-[44px]">
                      <p className="text-sm font-bold text-neutral-800">{a.target > 0 ? Math.round((a.achieved / a.target) * 100) : 0}%</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-800">{a.brand_name}</p>
                      <p className="text-[10px] text-neutral-400">{a.date}</p>
                    </div>
                  </div>
                  <Badge className={`text-[10px] capitalize ${a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {a.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── BRANCH MANAGER DASHBOARD ────────────────────────────────────────
function BranchDashboard() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);            // brand achievements
  const [pendingKonattu, setPendingKonattu] = useState([]);  // konattu entries
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [dRes, pRes, kRes] = await Promise.all([
        api.get('/dashboard/branch'),
        api.get('/achievements', { params: { status: 'pending' } }),
        api.get('/konattu-entries', { params: { status: 'pending' } }).catch(() => ({ data: [] }))
      ]);
      setData(dRes.data);
      setPending(pRes.data);
      setPendingKonattu(kRes.data || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => {
    fetchData();
    // Real-time sync: refresh every 20s + on tab/window focus
    const interval = setInterval(fetchData, 20000);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, []);

  const handleApprove = async (id) => {
    try {
      await api.put(`/achievements/${id}/approve`);
      toast.success('Brand achievement approved');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleApproveKonattu = async (id) => {
    try {
      await api.put(`/konattu-entries/${id}/approve`);
      toast.success('Konattu achievement approved');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkApprove = async () => {
    const ids = pending.map(p => p.id);
    if (ids.length === 0) return;
    try {
      const res = await api.post('/achievements/bulk-approve', { ids });
      toast.success(`Approved ${res.data.approved} brand entries`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <DashboardSkeleton />;

  const brandPendingCount = pending.length;
  const konattuPendingCount = pendingKonattu.length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="branch-dashboard">
      <div>
        <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Branch Control</h1>
        <p className="text-neutral-500 text-sm mt-0.5">Today's branch overview</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Staff" value={data.total_staff} iconBg="bg-blue-50" color="text-blue-600" testId="stat-branch-staff" />
        <StatCard icon={CalendarCheck} label="Present Today" value={data.today_present ?? 0} iconBg="bg-green-50" color="text-green-600" testId="stat-branch-present"
          onClick={() => navigate('/attendance?date=today&status=present')} />
        <StatCard icon={AlertTriangle} label="Half Day" value={data.today_half_day ?? 0} iconBg="bg-orange-50" color="text-orange-600" testId="stat-branch-halfday"
          onClick={() => navigate('/attendance?date=today&status=half_day')} />
        <StatCard icon={AlertTriangle} label="Absent" value={data.today_absent ?? 0} iconBg="bg-red-50" color="text-red-600" testId="stat-branch-absent"
          onClick={() => navigate('/attendance?date=today&status=absent')} />
        <StatCard icon={Lock} label="Locked" value={data.sales_locked} iconBg="bg-red-50" color="text-red-600" testId="stat-branch-locked"
          onClick={() => navigate('/compliance')} />
        <StatCard icon={AlertTriangle} label="No Updates" value={data.sales_no_updates} iconBg="bg-amber-50" color="text-amber-600" testId="stat-branch-no-updates" />
      </div>

      {/* Brand vs Konattu pending split */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Trophy} label="Brand Achv. Pending" value={brandPendingCount} iconBg="bg-amber-50" color="text-amber-600" testId="stat-branch-brand-pending"
          onClick={() => navigate('/achievements')} />
        <StatCard icon={Trophy} label="Konattu Achv. Pending" value={konattuPendingCount} iconBg="bg-purple-50" color="text-purple-600" testId="stat-branch-konattu-pending"
          onClick={() => navigate('/konattu-achievements')} />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button className="h-12 bg-red-600 hover:bg-red-700 text-white rounded-sm font-semibold justify-start px-5"
          onClick={() => navigate('/compliance')} data-testid="quick-compliance">
          <ShieldCheck className="h-5 w-5 mr-3" /> View Compliance Panel
        </Button>
        <Button variant="outline" className="h-12 rounded-sm font-semibold justify-start px-5 border-neutral-300"
          onClick={() => navigate('/attendance')} data-testid="quick-attendance-override">
          <CalendarCheck className="h-5 w-5 mr-3 text-green-600" /> Mark Attendance
        </Button>
        <Button variant="outline" className="h-12 rounded-sm font-semibold justify-start px-5 border-neutral-300"
          onClick={() => navigate('/leave?view=all&status=pending,manager_approved')} data-testid="quick-view-leaves">
          <CalendarOff className="h-5 w-5 mr-3 text-purple-600" /> Leave Requests ({data.pending_leaves})
        </Button>
      </div>

      {/* Brand Pending Approvals - 1-click approve */}
      {pending.length > 0 && (
        <Card className="border border-amber-200 shadow-sm rounded-md" data-testid="pending-approvals-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" /> Pending Approvals ({pending.length})
              </CardTitle>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs h-8"
                onClick={handleBulkApprove} data-testid="quick-bulk-approve">
                <Check className="h-3.5 w-3.5 mr-1" /> Approve All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1">
              {pending.slice(0, 8).map(a => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">{a.employee_name} &middot; {a.brand_name}</p>
                      <p className="text-[10px] text-neutral-400">{a.date} &middot; Target: {a.target?.toLocaleString()} | Achieved: {a.achieved?.toLocaleString()} ({a.target > 0 ? Math.round((a.achieved / a.target) * 100) : 0}%)</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 text-xs rounded-sm flex-shrink-0"
                    onClick={() => handleApprove(a.id)} data-testid={`quick-approve-${a.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                </div>
              ))}
              {pending.length > 8 && (
                <Button variant="ghost" size="sm" className="w-full text-xs text-red-600 mt-1" onClick={() => navigate('/achievements')}>
                  View all {pending.length} entries <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Konattu Pending Approvals - 1-click approve */}
      {pendingKonattu.length > 0 && (
        <Card className="border border-purple-200 shadow-sm rounded-md" data-testid="pending-konattu-approvals-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-purple-500" /> Konattu Pending Approvals ({pendingKonattu.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1">
              {pendingKonattu.slice(0, 8).map(a => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">{a.employee_name}</p>
                      <p className="text-[10px] text-neutral-400">{a.date} &middot; Konattu Achieved: {a.konattu_achieved?.toLocaleString?.() ?? a.konattu_achieved}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 text-xs rounded-sm flex-shrink-0"
                    onClick={() => handleApproveKonattu(a.id)} data-testid={`quick-approve-konattu-${a.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                </div>
              ))}
              {pendingKonattu.length > 8 && (
                <Button variant="ghost" size="sm" className="w-full text-xs text-purple-600 mt-1" onClick={() => navigate('/konattu-achievements')}>
                  View all {pendingKonattu.length} entries <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── CORPORATE DASHBOARD ─────────────────────────────────────────────
function CorporateDashboard() {
  const [data, setData] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get('/dashboard/corporate').then(r => setData(r.data)).catch(console.error);

  useEffect(() => {
    let cancelled = false;
    const reload = () => api.get('/dashboard/corporate').then(r => { if (!cancelled) setData(r.data); }).catch(console.error);
    reload();
    // Real-time sync: refresh every 20s + on tab/window focus
    const interval = setInterval(reload, 20000);
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, []);

  const handleCleanupOrphans = async () => {
    if (!window.confirm(
      'Cleanup Orphan Records?\n\n' +
      'This removes any leaves, achievements, attendance, tasks, payroll, etc. whose owning employee no longer exists. ' +
      'Useful when dashboard counts don\'t match the real data (usually from employees deleted before cascade-delete shipped).\n\n' +
      'Safe — does not touch records of active employees.'
    )) return;
    setCleaningUp(true);
    try {
      const res = await api.post('/admin/cleanup-orphans');
      const { purged, total_deleted } = res.data;
      if (total_deleted === 0) {
        toast.success('No orphan records found — your data is clean ✓');
      } else {
        const breakdown = Object.entries(purged).map(([k, v]) => `${k}: ${v}`).join(', ');
        toast.success(`Purged ${total_deleted} orphan record${total_deleted > 1 ? 's' : ''} (${breakdown})`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCleaningUp(false); }
  };

  if (!data) return <DashboardSkeleton />;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="corporate-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Corporate Control</h1>
          <p className="text-neutral-500 text-sm mt-0.5">All 15 branches overview</p>
        </div>
        {user?.system_role === 'super_admin' && (
          <Button variant="outline" size="sm" className="rounded-sm text-xs h-8 border-neutral-300"
            onClick={handleCleanupOrphans} disabled={cleaningUp} data-testid="cleanup-orphans-button">
            <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${cleaningUp ? 'animate-pulse' : ''}`} />
            {cleaningUp ? 'Cleaning…' : 'Cleanup Orphan Records'}
          </Button>
        )}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total Staff" value={data.total_employees} iconBg="bg-blue-50" color="text-blue-600" testId="stat-total-staff" />
        <StatCard icon={CalendarCheck} label="Present Today" value={data.today_present ?? 0} iconBg="bg-green-50" color="text-green-600" testId="stat-today-present"
          onClick={() => navigate('/attendance?date=today&status=present')} />
        <StatCard icon={AlertTriangle} label="Half Day Today" value={data.today_half_day ?? 0} iconBg="bg-orange-50" color="text-orange-600" testId="stat-today-halfday"
          onClick={() => navigate('/attendance?date=today&status=half_day')} />
        <StatCard icon={AlertTriangle} label="Absent Today" value={data.today_absent ?? 0} iconBg="bg-red-50" color="text-red-600" testId="stat-today-absent"
          onClick={() => navigate('/attendance?date=today&status=absent')} />
        <StatCard icon={Lock} label="Locked" value={data.locked_count} iconBg="bg-red-50" color="text-red-600" testId="stat-locked"
          onClick={() => navigate('/compliance')} />
        <StatCard icon={Trophy} label="Pending Approvals" value={data.pending_konattu_achievements ?? 0} iconBg="bg-amber-50" color="text-amber-600" testId="stat-pending"
          onClick={() => navigate('/konattu-achievements')} />
      </div>
      {data.today_unmarked > 0 && (
        <p className="text-[11px] text-neutral-500 -mt-2" data-testid="dashboard-unmarked-note">
          {data.today_unmarked} staff have not yet marked attendance today (auto-marked absent at 12 PM / 11 PM IST).
        </p>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Button className="h-12 bg-red-600 hover:bg-red-700 text-white rounded-sm font-semibold justify-start px-5"
          onClick={() => navigate('/compliance')} data-testid="quick-compliance">
          <ShieldCheck className="h-5 w-5 mr-3" /> Compliance Panel
        </Button>
        <Button variant="outline" className="h-12 rounded-sm font-semibold justify-start px-5 border-neutral-300"
          onClick={() => navigate('/attendance')} data-testid="quick-my-attendance">
          <CalendarCheck className="h-5 w-5 mr-3 text-green-600" /> My Attendance
        </Button>
        <Button variant="outline" className="h-12 rounded-sm font-semibold justify-start px-5 border-neutral-300"
          onClick={() => navigate('/achievements')} data-testid="quick-achievements">
          <Trophy className="h-5 w-5 mr-3 text-amber-600" /> Brand Approvals ({data.pending_achievements})
        </Button>
        <Button variant="outline" className="h-12 rounded-sm font-semibold justify-start px-5 border-neutral-300"
          onClick={() => navigate('/analytics')} data-testid="quick-analytics">
          <Building2 className="h-5 w-5 mr-3 text-blue-600" /> Branch Analytics
        </Button>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className={`border shadow-sm rounded-md ${data.no_updates_count > 0 ? 'border-amber-200 bg-amber-50/40' : 'border-neutral-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">No Updates Today</p>
            </div>
            <p className="text-2xl font-heading font-black text-amber-600">{data.no_updates_count}</p>
            <p className="text-[10px] text-neutral-400">of {data.total_sales_staff} sales staff</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm rounded-md cursor-pointer transition-colors ${(data.pending_leaves + (data.pending_leaves_manager_approved||0)) > 0 ? 'border-purple-200 bg-purple-50/40 hover:bg-purple-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
              onClick={() => navigate('/leave?view=all&status=pending,manager_approved')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarOff className="h-4 w-4 text-purple-500" />
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-600">Pending Leaves</p>
            </div>
            <p className="text-2xl font-heading font-black text-purple-600">{(data.pending_leaves || 0) + (data.pending_leaves_manager_approved || 0)}</p>
            <p className="text-[10px] text-neutral-600">{data.pending_leaves_manager_approved || 0} awaiting SA · {data.pending_leaves || 0} new</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm rounded-md cursor-pointer transition-colors ${(data.pending_emergency_leaves || 0) > 0 ? 'border-red-200 bg-red-50/50 hover:bg-red-50 ring-1 ring-red-200' : 'border-neutral-200 hover:bg-neutral-50'}`}
              onClick={() => navigate('/leave?view=all&filter=emergency')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-600">Emergency Leaves</p>
            </div>
            <p className="text-2xl font-heading font-black text-red-600">{data.pending_emergency_leaves || 0}</p>
            <p className="text-[10px] text-neutral-600">direct → Super Admin</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm rounded-md cursor-pointer transition-colors ${(data.pending_requests || 0) > 0 ? 'border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
              onClick={() => navigate('/requests')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shirt className="h-4 w-4 text-indigo-500" />
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-600">Requests</p>
            </div>
            <p className="text-2xl font-heading font-black text-indigo-600">{data.pending_requests || 0}</p>
            <p className="text-[10px] text-neutral-600">uniform / ID card</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm rounded-md ${data.pending_tasks > 0 ? 'border-blue-200 bg-blue-50/40' : 'border-neutral-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ListTodo className="h-4 w-4 text-blue-500" />
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-600">Pending Tasks</p>
            </div>
            <p className="text-2xl font-heading font-black text-blue-600">{data.pending_tasks}</p>
            <p className="text-[10px] text-neutral-600">awaiting completion</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-neutral-200 rounded animate-pulse" />)}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => <div key={i} className="h-12 bg-neutral-200 rounded animate-pulse" />)}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.system_role === 'super_admin' || user?.system_role === 'management') return <CorporateDashboard />;
  if (user?.system_role === 'branch_manager') return <BranchDashboard />;
  return <EmployeeDashboard />;
}
